import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Icon } from '@iconify/react';
import QRCode from 'qrcode';
import { MapContainer, TileLayer, Marker, Popup, useMap, GeoJSON } from 'react-leaflet';
import { useSearchParams } from 'react-router-dom';
import { labelForStatus, levelFromReference, currentEscalationLevel } from '../../utils/escalation';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import './MapComponent.css';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Status color mapping
const getStatusClasses = (status) => {
  const normalizedStatus = status.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
  
  switch (normalizedStatus) {
    case "total":
      return { bg: "bg-info-focus", text: "text-white" };
    case "open":
      return { bg: "bg-warning-focus", text: "text-warning-600" };
    case "inprogress":
    case "in progress":
      return { bg: "bg-info-focus", text: "text-info-600" };
    case "closed":
      return { bg: "bg-success-focus", text: "text-success-600" };
    case "escalated":
      return { bg: "bg-danger-focus", text: "text-danger-600" };
    case "reopened":
    case "Reopened":
      return { bg: "bg-secondary-focus", text: "text-secondary-600" };
      case "resolved":
    case "Resolved":
      return { bg: "bg-primary-focus", text: "text-info-100" };
    case "cancelled":
      return { bg: "bg-danger-focus", text: "text-danger-600" };
    default:
      return { bg: "bg-secondary", text: "text-secondary-600" };
  }
};

// Predefined colors for sources
const sourceColors = {
  "Helpline": "#487fff",
  "Chatbot": "#22d3ee",
  "Whatsapp": "#16a34a",
  "Social Media": "#2563eb",
  "By Post": "#ff9f29",
  "By Hand": "#fbbf24",
  "By Email": "#f472b6",
  "Public Form": "#dc2626",
  "Other": "#4b5563"
};

// Memoized icon creation
const createCustomIcon = (color, letter) => {
  return new L.DivIcon({
    html: `<div style="
      background: transparent;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <div style="
        background-color: ${color};
        width: 24px;
        height: 24px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          transform: rotate(45deg);
          color: white;
          font-weight: bold;
          font-size: 10px;
        ">${letter}</div>
      </div>
    </div>`,
    className: "",
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24]
  });
};

// A distinct, larger pulsing pin used to highlight a single searched complaint.
const createFocusIcon = () => new L.DivIcon({
  html: `<div class="map-focus-pin"><div class="map-focus-pulse"></div>
    <div style="background-color:#e11d48;width:34px;height:34px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4);">
      <div style="transform:rotate(45deg);color:#fff;font-weight:bold;font-size:14px;">★</div>
    </div></div>`,
  className: '',
  iconSize: [34, 34],
  iconAnchor: [17, 34],
  popupAnchor: [0, -34],
});

// Smoothly recentres the map on a searched complaint.
const FlyToPoint = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng)) {
      map.flyTo([lat, lng], 13, { duration: 1.2 });
    }
  }, [lat, lng, map]);
  return null;
};

// Maps status background utility classes to their hex color
const statusColorMap = {
  "bg-warning-focus": "#ff9f29",
  "bg-info-focus": "#2563eb",
  "bg-success-focus": "#16a34a",
  "bg-danger-focus": "#dc2626",
  "bg-secondary-focus": "#4b5563",
  "bg-primary-focus": "#b66dff",
  "bg-secondary": "#4b5563"
};

// Marker colours matched to the dashboard status cards.
const STATUS_CARD_COLOR = {
  total: '#4338ca',
  open: '#b45309',
  inprogress: '#4338ca',
  closed: '#0369a1',
  escalated: '#b91c1c',
  reopened: '#0f766e',
  resolved: '#15803d',
  cancelled: '#b91c1c',
};

const getStatusColor = (status) => {
  const norm = String(status || '').toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
  return STATUS_CARD_COLOR[norm] || statusColorMap[getStatusClasses(status).bg] || '#6c757d';
};

// Memoized status icons
const getStatusIcon = (status) => {
  return createCustomIcon(getStatusColor(status), status.charAt(0).toUpperCase());
};

// Memoized source icons
const getSourceIcon = (source) => {
  const color = sourceColors[source] || "#4b5563";
  return createCustomIcon(color, source.charAt(0).toUpperCase());
};

// Component to set the initial view and map bounds
const SetMapBounds = () => {
  const map = useMap();
  
  useEffect(() => {
    // Set view to center of Punjab
    map.fitBounds([
      [32.5007, 74.5237], // North-West
      [29.3822, 71.5249]  // South-East
    ]);
  }, [map]);

  return null;
};

// Forces Leaflet to recalculate its size after the container's dimensions change (e.g. fullscreen toggle)
const MapResizeHandler = ({ trigger }) => {
  const map = useMap();

  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 200);
    return () => clearTimeout(timer);
  }, [trigger, map]);

  return null;
};

// Component to render district labels
const DistrictLabels = ({ districts }) => {
  const map = useMap();

  useEffect(() => {
    if (!districts) return;

    // Create a layer group for labels
    const labelLayer = L.layerGroup().addTo(map);

    districts.features.forEach(feature => {
      if (feature.properties?.district_n) {
        // Calculate centroid for the district
        const coordinates = feature.geometry.coordinates;
        let centerLat = 0, centerLng = 0, pointCount = 0;

        // Handle both Polygon and MultiPolygon
        const polygons = feature.geometry.type === 'Polygon' ? [coordinates] : coordinates;
        
        polygons.forEach(polygon => {
          polygon[0].forEach(coord => {
            centerLng += coord[0];
            centerLat += coord[1];
            pointCount++;
          });
        });

        if (pointCount > 0) {
          centerLat /= pointCount;
          centerLng /= pointCount;

          // Create the label
          const label = L.marker([centerLat, centerLng], {
            icon: L.divIcon({
              className: 'district-label',
              html: `<div style="
                      display: inline-block;
                      background: rgba(134, 176, 248, 0.7);
                      padding: 2px 8px;
                      border-radius: 4px;
                      font-weight: bold;
                      font-size: 12px;
                      color: #333;
                      text-shadow: 1px 1px 1px white;
                      white-space: nowrap;
                    ">
                      ${feature.properties.district_n}
                    </div>`,
              iconSize: [0, 0],
              iconAnchor: [0, 0]
            }),
            zIndexOffset: -1000
          });

          label.addTo(labelLayer);
        }
      }
    });

    return () => {
      map.removeLayer(labelLayer);
    };
  }, [map, districts]);

  return null;
};

const MapComponent = () => {
  const [loading, setLoading] = useState(true);
  const [complaintsByGroup, setComplaintsByGroup] = useState({});
  const [error, setError] = useState(null);
  const [groupBy, setGroupBy] = useState("status");
  const [isChangingGroup, setIsChangingGroup] = useState(false);
  // Zone filter is display-only for now (chips disabled); keep it fixed to "all".
  const [selectedZone] = useState(null); // null = all zones
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
  const APP_BASE_URL = process.env.REACT_APP_URL;
  const [districts, setDistricts] = useState(null);
  const [complaintDetails, setComplaintDetails] = useState({});
  const [detailLoadingId, setDetailLoadingId] = useState(null);
  const [detailQrCodes, setDetailQrCodes] = useState({});
  const [isFullscreen, setIsFullscreen] = useState(false);
  const mapWrapperRef = useRef(null);

  // Single-complaint search / focus
  const [mapSearch, setMapSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [focused, setFocused] = useState(null); // { id, number, lat, lng, status, ... }
  const [searchParams] = useSearchParams();
  const focusMarkerRef = useRef(null);

  // Locate a single complaint by its ID / complaint number and pin it on the map.
  const focusComplaintByQuery = useCallback(async (rawQuery) => {
    const query = (rawQuery || '').trim();
    if (!query) return;
    setSearching(true);
    setSearchError('');
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ searchQuery: query, perPage: '5', pageNo: '1' });
      const response = await fetch(`${API_BASE_URL}/backend/complaints/get-all-complaints?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        if (response.status === 404) { setSearchError(`No complaint found for “${query}”.`); setFocused(null); return; }
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      const list = data?.data || [];
      const match = list.find(c => (c.complaint_number || '').toLowerCase() === query.toLowerCase()) || list[0];
      if (!match) { setSearchError(`No complaint found for “${query}”.`); setFocused(null); return; }
      const lat = parseFloat(match.lat);
      const lng = parseFloat(match.long);
      if (Number.isNaN(lat) || Number.isNaN(lng)) {
        setSearchError(`Complaint ${match.complaint_number || query} has no map location recorded.`);
        setFocused(null);
        return;
      }
      setFocused({
        id: match._id,
        number: match.complaint_number,
        lat, lng,
        status: match.status,
        escalationLevel: currentEscalationLevel(match),
        source: match.complaint_source,
        nature: match.complaint_nature_id?.name,
        priority: match.priority_level,
        date: match.time_of_call,
        address: [match.near_by_place, match.complaint_address].filter(Boolean).join(', '),
      });
    } catch (err) {
      console.error('Map search failed:', err);
      setSearchError('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  }, [API_BASE_URL]);

  const handleMapSearch = (e) => {
    if (e) e.preventDefault();
    focusComplaintByQuery(mapSearch);
  };

  const clearFocus = () => {
    setFocused(null);
    setSearchError('');
    setMapSearch('');
  };

  // Auto-focus when arriving via /dashboard?focusComplaint=<id/number> (e.g. the
  // "View on map" button in the complaints table).
  useEffect(() => {
    const fc = searchParams.get('focusComplaint');
    if (fc) {
      setMapSearch(fc);
      focusComplaintByQuery(fc);
      // Bring the map into view since the cards sit above it on the dashboard.
      setTimeout(() => mapWrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    }
  }, [searchParams, focusComplaintByQuery]);

  // Open the focused complaint's popup automatically once its marker mounts.
  useEffect(() => {
    if (focused && focusMarkerRef.current) {
      const t = setTimeout(() => { try { focusMarkerRef.current.openPopup(); } catch (_) {} }, 400);
      return () => clearTimeout(t);
    }
  }, [focused]);

  // Memoized flattened complaints array, tagging each point with its group key
  const allComplaints = useMemo(() => {
    return Object.entries(complaintsByGroup).flatMap(([groupKey, points]) =>
      points.map(point => ({ ...point, groupKey }))
    );
  }, [complaintsByGroup]);

  // Points to actually render: all zones, or only the selected zone's pins.
  const visibleComplaints = useMemo(
    () => (selectedZone ? allComplaints.filter((c) => c.zone === selectedZone) : allComplaints),
    [allComplaints, selectedZone]
  );

  // Optimized fetch function with abort controller
  const fetchComplaints = useCallback(async (group, signal) => {
    setLoading(true);
    setIsChangingGroup(true);
    setComplaintsByGroup({});
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/backend/complaints/status-wise-map-cordinates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ groupBy: group }),
        signal
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      if (data.status && data.data) {
        // Drop groups that shouldn't appear on the map: the deprecated "Cancelled"
        // status, and "Open" (not dispatched/geo-tagged yet, so no meaningful pin).
        const HIDDEN_GROUPS = ['cancelled', 'open'];
        const cleaned = Object.fromEntries(
          Object.entries(data.data).filter(([key]) => !HIDDEN_GROUPS.includes(String(key).toLowerCase().replace(/\s+/g, '')))
        );
        setComplaintsByGroup(cleaned);
      } else {
        throw new Error('Invalid data format');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error fetching complaints:', err);
        setError(err.message);
      }
    } finally {
      setLoading(false);
      setIsChangingGroup(false);
    }
  }, [API_BASE_URL]);

  // Fetches full complaint details (and generates its QR code) when a marker is clicked
  const fetchComplaintDetail = useCallback(async (id) => {
    setDetailLoadingId(id);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/backend/complaints/complaint-info-for-map`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id })
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      if (data.status && data.data) {
        setComplaintDetails(prev => ({ ...prev, [id]: data.data }));

        const qrTargetUrl = `${APP_BASE_URL}/complains/view/${id}`;
        try {
          const qrImageUrl = await QRCode.toDataURL(qrTargetUrl, { width: 200 });
          setDetailQrCodes(prev => ({ ...prev, [id]: { imageUrl: qrImageUrl, targetUrl: qrTargetUrl } }));
        } catch (qrErr) {
          console.error('Error generating QR code:', qrErr);
        }
      }
    } catch (err) {
      console.error('Error fetching complaint detail:', err);
    } finally {
      setDetailLoadingId(null);
    }
  }, [API_BASE_URL, APP_BASE_URL]);

  // Toggles browser fullscreen on the map wrapper
  const toggleFullscreen = () => {
    const el = mapWrapperRef.current;
    if (!el) return;

    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  // Handle groupBy change with loading state
  const handleGroupByChange = (e) => {
    setGroupBy(e.target.value);
    setIsChangingGroup(true);
  };

  // Main data fetching effect with cleanup
  useEffect(() => {
    const abortController = new AbortController();
    
    fetchComplaints(groupBy, abortController.signal);
    
    return () => {
      abortController.abort();
    };
  }, [groupBy, fetchComplaints]);

  // Load districts data
  useEffect(() => {
    const loadDistricts = async () => {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL}/assets/districts_punjab.geojson`);
        const data = await response.json();
        setDistricts(data);
      } catch (err) {
        console.error('Error loading GeoJSON:', err);
      }
    };

    loadDistricts();
  }, []);

  // Track fullscreen state as the user enters/exits via the button or Esc key
  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Memoized markers to prevent unnecessary re-renders
  const markers = useMemo(() => {
    if (isChangingGroup) return null;

    return visibleComplaints.map(complaint => {
      // Ensure coordinates are valid numbers
      const lat = parseFloat(complaint.lat);
      const lng = parseFloat(complaint.lng);
      
      if (isNaN(lat) || isNaN(lng)) {
        console.warn('Invalid coordinates for complaint:', complaint);
        return null;
      }

      const detail = complaintDetails[complaint.id];
      const qr = detailQrCodes[complaint.id];
      const isDetailLoading = detailLoadingId === complaint.id && !detail;

      return (
        <Marker
          key={`${complaint.id}-${groupBy}`}
          position={[lat, lng]}
          icon={
            groupBy === "status"
              ? getStatusIcon(complaint.groupKey)
              : getSourceIcon(complaint.groupKey)
          }
          eventHandlers={{
            click: () => {
              if (!complaintDetails[complaint.id]) {
                fetchComplaintDetail(complaint.id);
              }
            }
          }}
        >
          <Popup className="complaint-popup">
            {isDetailLoading ? (
              <div className="complaint-popup-body">
                <Skeleton height={18} width="70%" style={{ marginBottom: '10px' }} />
                <Skeleton height={12} count={4} />
              </div>
            ) : detail ? (
              <div className="complaint-popup-card">
                <div className="complaint-popup-header" style={{ background: getStatusColor(detail.status) }}>
                  {labelForStatus(detail.status, levelFromReference(detail.status, detail.escalation_reference))}
                </div>
                <div className="complaint-popup-body">
                  <div className="complaint-popup-top">
                    <h6 className="complaint-popup-title">{detail.title}</h6>
                    {qr?.imageUrl && (
                      <a
                        href={qr.targetUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="complaint-popup-qr"
                      >
                        <img src={qr.imageUrl} alt="Complaint QR Code" />
                      </a>
                    )}
                  </div>

                  <hr className="complaint-popup-divider" />

                  <div className="complaint-popup-grid">
                    <div>
                      <span className="complaint-popup-label">Priority</span>
                      <span className="complaint-popup-value">{detail.priority}</span>
                    </div>
                    <div>
                      <span className="complaint-popup-label">Date</span>
                      <span className="complaint-popup-value">{new Date(detail.date).toLocaleDateString()}</span>
                    </div>
                    <div>
                      <span className="complaint-popup-label">Status</span>
                      <span className={`px-3 py-1 rounded-pill fw-semibold ${getStatusClasses(detail.status).bg} ${getStatusClasses(detail.status).text}`}>
                        {labelForStatus(detail.status, levelFromReference(detail.status, detail.escalation_reference))}
                      </span>
                    </div>
                    <div>
                      <span className="complaint-popup-label">Source</span>
                      <span className="complaint-popup-value">{detail.complaint_source}</span>
                    </div>
                  </div>

                  <div>
                    <span className="complaint-popup-label">Complaint Details</span>
                    <span
                      className="complaint-popup-value complaint-popup-detail"
                      title={detail.complaint_detail || 'No details provided'}
                    >
                      {detail.complaint_detail || 'No details provided'}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="complaint-popup-body">Complaint ID: {complaint.id}</div>
            )}
          </Popup>
        </Marker>
      );
    }).filter(Boolean);
  }, [visibleComplaints, groupBy, isChangingGroup, complaintDetails, detailQrCodes, detailLoadingId, fetchComplaintDetail]);

  return (
    <div className="col-xxl-12">
      <div className="card h-100">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
            <h6 className="fw-bold mb-0">Complaints Map</h6>
            <div className="d-flex align-items-center flex-wrap gap-2">
              <form onSubmit={handleMapSearch} className="d-flex align-items-center gap-2">
                <div className="input-group input-group-sm" style={{ width: '240px' }}>
                  <span className="input-group-text"><Icon icon="mdi:magnify" /></span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Find complaint by ID…"
                    value={mapSearch}
                    onChange={(e) => setMapSearch(e.target.value)}
                  />
                  {(focused || mapSearch) && (
                    <button type="button" className="btn btn-outline-secondary" onClick={clearFocus} title="Clear">
                      <Icon icon="mdi:close" />
                    </button>
                  )}
                </div>
                <button type="submit" className="btn btn-sm btn-success" disabled={searching}>
                  {searching ? '…' : 'Search'}
                </button>
              </form>
              <select
                className="form-select form-select-sm w-auto"
                value={groupBy}
                onChange={handleGroupByChange}
                disabled={loading}
              >
                <option value="status">Group by Status</option>
                <option value="source">Group by Source</option>
              </select>
            </div>
          </div>
          {searchError && (
            <div className="alert alert-warning py-1 px-2 mb-2 small d-flex align-items-center gap-2">
              <Icon icon="mdi:alert-outline" /> {searchError}
            </div>
          )}
          {focused && !searchError && (
            <div className="alert alert-info py-1 px-2 mb-2 small d-flex align-items-center gap-2">
              <Icon icon="mdi:map-marker-star" /> Showing complaint <strong>{focused.number}</strong> on the map.
            </div>
          )}

          {/* Plotted vs total — explains why fewer pins show than the dashboard total. */}
          {/* {mapSummary && (
            <div className="text-secondary-light mb-2" style={{ fontSize: 12.5 }}>
              <Icon icon="mdi:map-marker-check" className="me-1" />
              Showing <strong>{allComplaints.length}</strong> of <strong>{mapSummary.total}</strong> complaints on the map.
              {mapSummary.total - allComplaints.length > 0 && (
                <span> The remaining {mapSummary.total - allComplaints.length} are not shown because they have no map location{mapSummary.missingCoordinates > 0 ? ` (${mapSummary.missingCoordinates} not geo-tagged)` : ''} or are still Open (not dispatched).</span>
              )}
            </div>
          )} */}

          {/* Zone filter — click a zone to load only its pins. */}
          {/* <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
            <span className="text-secondary-light fw-semibold" style={{ fontSize: 13 }}>Filter by zone:</span>
            {[
              { key: null, label: 'All', color: '#6366f1', count: zoneCounts.Northern + zoneCounts.Central + zoneCounts.Southern },
              { key: 'Northern', label: 'Northern', color: '#f59e0b', count: zoneCounts.Northern },
              { key: 'Central', label: 'Central', color: '#3b82f6', count: zoneCounts.Central },
              { key: 'Southern', label: 'Southern', color: '#22c55e', count: zoneCounts.Southern },
            ].map((z) => {
              const active = selectedZone === z.key;
              return (
                <button
                  key={z.label}
                  type="button"
                  aria-disabled="true"
                  onClick={(e) => e.preventDefault()}
                  className="btn btn-sm d-flex align-items-center gap-2"
                  style={{
                    border: `1px solid ${z.color}`,
                    background: active ? z.color : `${z.color}14`,
                    color: active ? '#fff' : z.color,
                    fontWeight: 700,
                    borderRadius: 999,
                    cursor: 'default',
                    pointerEvents: 'none',
                  }}
                >
                  {z.label}
                  <span className="badge" style={{ background: active ? 'rgba(255,255,255,.25)' : `${z.color}22`, color: active ? '#fff' : z.color }}>{z.count}</span>
                </button>
              );
            })}
          </div> */}

          <div
            ref={mapWrapperRef}
            className="mt-2"
            style={{ height: isFullscreen ? '100vh' : '600px', borderRadius: isFullscreen ? 0 : '8px', overflow: 'hidden', position: 'relative', background: '#fff' }}
          >
            {loading || isChangingGroup ? (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2 }}>
                <Skeleton height="100%" />
              </div>
            ) : error ? (
              <div className="alert alert-danger">{error}</div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  className="d-flex align-items-center justify-content-center"
                  title={isFullscreen ? 'Exit full screen' : 'View full screen'}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    zIndex: 1000,
                    width: '34px',
                    height: '34px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    background: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  <Icon icon={isFullscreen ? 'solar:quit-full-screen-linear' : 'solar:full-screen-linear'} width="18" height="18" />
                </button>
                <MapContainer
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom={true}
                  preferCanvas={true}
                >
                  <MapResizeHandler trigger={isFullscreen} />
                  <SetMapBounds />
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    attribution='Tiles © Esri'
                  />
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                    attribution='Tiles © Esri'
                  />

                  {districts && (
                    <>
                      <GeoJSON
                        data={districts}
                        style={() => ({
                          color: '#3388ff',
                          weight: 2,
                          opacity: 1,
                          fillOpacity: 0.1
                        })}
                      />
                      <DistrictLabels districts={districts} />
                    </>
                  )}

                  {markers}

                  {/* Highlighted single complaint from a search / deep link */}
                  {focused && (
                    <>
                      <FlyToPoint lat={focused.lat} lng={focused.lng} />
                      <Marker
                        position={[focused.lat, focused.lng]}
                        icon={createFocusIcon()}
                        ref={focusMarkerRef}
                        zIndexOffset={2000}
                      >
                        <Popup className="complaint-popup">
                          <div className="complaint-popup-card">
                            <div className="complaint-popup-header" style={{ background: getStatusColor(focused.status || 'Open') }}>
                              {labelForStatus(focused.status, focused.escalationLevel) || 'Complaint'}
                            </div>
                            <div className="complaint-popup-body">
                              <h6 className="complaint-popup-title">{focused.number}</h6>
                              <hr className="complaint-popup-divider" />
                              <div className="complaint-popup-grid">
                                <div><span className="complaint-popup-label">Nature</span><span className="complaint-popup-value">{focused.nature || '-'}</span></div>
                                <div><span className="complaint-popup-label">Priority</span><span className="complaint-popup-value">{focused.priority || '-'}</span></div>
                                <div><span className="complaint-popup-label">Source</span><span className="complaint-popup-value">{focused.source || '-'}</span></div>
                                <div><span className="complaint-popup-label">Date</span><span className="complaint-popup-value">{focused.date ? new Date(focused.date).toLocaleDateString() : '-'}</span></div>
                              </div>
                              <div><span className="complaint-popup-label">Address</span><span className="complaint-popup-value">{focused.address || '-'}</span></div>
                              <a href={`${APP_BASE_URL}/complains/view/${focused.id}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary mt-2 w-100">
                                Open full complaint
                              </a>
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    </>
                  )}
                </MapContainer>
              </>
            )}
          </div>

          <div className="mt-3">
            <div className="d-flex flex-wrap gap-3">
              {Object.keys(complaintsByGroup).map(key => {
                const color = groupBy === "status" ? getStatusColor(key) : (sourceColors[key] || '#4b5563');
                const count = visibleComplaints.filter(c => c.groupKey === key).length;
                if (selectedZone && count === 0) return null;
                return (
                  <div key={key} className="d-flex align-items-center">
                    <div
                      style={{
                        backgroundColor: color,
                        width: '16px',
                        height: '16px',
                        borderRadius: '50% 50% 50% 0',
                        transform: 'rotate(-45deg)',
                        marginRight: '8px'
                      }}
                    />
                    <span className="text-capitalize">{key}</span>
                    <span className="ms-1">({count})</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapComponent;